import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { AlarmHistoryDetailOverlayComponent } from '@components/alarm-history-detail-overlay/alarm-history-detail-overlay.component';
import { fadeInOut } from '@helpers/fadeInOut';
import { CssVarService } from '@service/css-vars.service';
import { AlarmHistoryService } from '@service/ops-alarms/alarm-history.service';
import { PaginationStrategy } from '@service/pagination-strategy.service';
import { ResponsiveService } from '@service/responsive.service';
import { StaticPaginationStrategy } from '@service/static-pagination-strategy';
import dayjs from 'dayjs';
import { AlarmHistoryData } from 'ops/common/models/alarm-history-data';
import { Subject } from 'rxjs';
import { DynamicMaxHeightDirective } from 'src/app/directives/alarm-table-height/alarm-table-height.directive';
import { OpsAlarmChild } from '../ops-alarm.component';

const DEFAULT_ITEMS_PER_PAGE = 6;
@Component({
    selector: 'app-ops-alarm-history',
    templateUrl: './ops-alarm-history.component.html',
    styleUrls: ['./ops-alarm-history.component.scss'],
    animations: [fadeInOut],
    providers: [
        {
            provide: PaginationStrategy,
            useValue: new StaticPaginationStrategy(DEFAULT_ITEMS_PER_PAGE),
        },
    ],
    standalone: false
})
export class OpsAlarmHistoryComponent implements OnInit {
    @ViewChild('dynamicMaxHeight') dynamicMaxHeight: DynamicMaxHeightDirective;
    @ViewChild('filters') filters: ElementRef;
    @ViewChild('alarmDetailOverlay') alarmDetailOverlay: AlarmHistoryDetailOverlayComponent;
    @ViewChild('tableContent') tableContent: ElementRef;
    @ViewChild('resetFilterButton') resetFilterButton: ElementRef;
    @Output() onBackEmit = new EventEmitter();
    @Output() onBackToTop = new EventEmitter();

    readonly pageTitle = OpsAlarmChild.History;

    protected _currentPage = 0;
    private _isLoading = false;

    alarmHistoryData: AlarmHistoryData[] = [];
    alarmHistoryDetail: AlarmHistoryData;
    removeHeightLimits: boolean = false;
    clickRefreshSubject: Subject<boolean> = new Subject();
    filterOption: FilterOptions = {
        status: AlarmLevels.UNDEFINED,
        channel: '',
        occurrenceAt: '',
        recoveryTime: '',
    };
    savedFilters: FilterOptions | null = null;
    displayResetOption: boolean = false;
    showSearchDivider: boolean = true;
    filtredAlarmHistoryData: AlarmHistoryData[] = [];

    showCalendars: boolean = false

    constructor(
        private alarmHistoryService: AlarmHistoryService,
        public responsive: ResponsiveService,
        protected paginationStrategy: PaginationStrategy,
        private cssVarService: CssVarService,
    ) {}

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.shouldRemoveHeightLimit();
    }

    @HostListener('document:click', ['$event'])
    handleClick(event: Event) {
        if (
            this.displayResetOption &&
            !this.resetFilterButton.nativeElement.contains(event.target)
        ) {
            this.displayResetOption = false;
        }
    }

    async ngOnInit() {
        this.alarmHistoryData = await this.alarmHistoryService.getAlarmHistoryDataAPI();
        this.dataFilter();
        this.paginationStrategy.recalculate(this.filtredAlarmHistoryData.length);
        this.shouldRemoveHeightLimit();
    }

    ngAfterViewInit(): void {
        const tableRowHeightAlarm = this.cssVarService.tableRowHeightAlarm;
        const tableRowGap = this.cssVarService.tableRowGap;
        this.dynamicMaxHeight.init(
            tableRowHeightAlarm,
            tableRowGap,
            DEFAULT_ITEMS_PER_PAGE,
            this.filters.nativeElement.offsetHeight,
        );
    }

    public dataFilter() {
        let newAlarmData = this.alarmHistoryData;

        newAlarmData = newAlarmData.filter((data: AlarmHistoryData) =>
            this.filterOption.status !== ''
                ? data.status === this.filterOption.status.toUpperCase()
                : data,
        );

        newAlarmData = newAlarmData.filter((data: AlarmHistoryData) =>
            this.filterOption.channel !== ''
                ? data.channel === this.filterOption.channel ||
                  data.name === this.filterOption.channel.toUpperCase()
                : data,
        );

        newAlarmData = newAlarmData.filter((data: AlarmHistoryData) =>
            this.filterOption.recoveryTime !== ''
                ? dayjs(new Date(data.recoveryTime)).format('YYYY/MM/DD') ===
                  this.filterOption.recoveryTime
                : data,
        );

        newAlarmData = newAlarmData.filter((data: AlarmHistoryData) =>
            this.filterOption.occurrenceAt !== ''
                ? dayjs(new Date(data.occurrenceAt)).format('YYYY/MM/DD') ===
                  this.filterOption.occurrenceAt
                : data,
        );

        this.filtredAlarmHistoryData = newAlarmData;
        this.calculatePagination();
    }

    resetFilter() {
        this.saveFilters();
        const keys = Object.keys(this.filterOption);
        for (let i = 0; i < keys.length; i++) {
            if (this.filterOption[keys[i]] !== '') {
                if (!this.savedFilters) {
                    this.saveFilters();
                }
                this.filterOption[keys[i]] = '';
            }
        }
        this.dataFilter();
    }

    refreshFilters() {
        this.displayResetOption = false;
        this.filterOption = {
            ...this.savedFilters,
        };
        this.savedFilters = null;
    }

    onSearchClick() {
        this.displayResetOption = false;
    }

    saveFilters() {
        this.displayResetOption = true;
        this.savedFilters = {
            ...this.filterOption,
        };
    }

    deleteFilterCondition() {
        const keys = Object.keys(this.filterOption);
        for (let i = 0; i < Object.keys(this.filterOption).length; i++) {
            this.filterOption[keys[i]] = '';
        }
    }

    isClearFilterVisible() {
        if (this.displayResetOption) {
            return false;
        }

        let hasFilterOptions = false;
        const values = Object.values(this.filterOption);
        for (let i = 0; i < values.length; i++) {
            if (values[i] !== '') {
                hasFilterOptions = true;
            }
        }
        return hasFilterOptions;
    }

    calculatePagination() {
        this.paginationStrategy.recalculate(this.filtredAlarmHistoryData.length);
    }

    handleCalendarDate(event: { name: CalendarOptions; value: string }) {
        if (event.name == CalendarOptions.OCCURRENCE) {
            this.filterOption.occurrenceAt = event.value;
        } else if (event.name == CalendarOptions.RECOVERY) {
            this.filterOption.recoveryTime = event.value;
        }
    }

    async onClickRefresh() {
        this.filtredAlarmHistoryData = [];
        this.alarmHistoryData = await this.alarmHistoryService.getAlarmHistoryDataAPI();
        this.dataFilter();
        this.calculatePagination();
    }

    onDetail(alarmHistory: AlarmHistoryData) {
        this.alarmHistoryDetail = alarmHistory;
        this.alarmDetailOverlay.setVisible(true);
    }

    displayedPageLimitCount() {
        if (this.responsive.isLarge() || this.responsive.isXLarge()) {
            return 7;
        } else {
            return 5;
        }
    }

    onSearchInputChange() {
        if (!this.filterOption.channel.trim()) {
            this.onSearchClick();
            this.dataFilter();
        }
    }

    onBack() {
        this.onBackEmit.emit();
    }

    onScroll() {
        if (this.responsive.isXSmall() || this.responsive.isHandsetLandscape()) {
            const { scrollTop, scrollHeight, clientHeight } = this.tableContent.nativeElement;
            if (scrollTop + clientHeight >= scrollHeight - 5 && !this.isLoading) {
                this.loadMoreItems();
            }
        }
    }

    loadMoreItems() {
        this._currentPage = Math.min(this._currentPage + 1, this.pageCount);
    }

    onTop() {
        this.onBackToTop.emit()
    }

    isScrollBtnVisible() {
        if (this.tableContent) {
            const rect = this.tableContent.nativeElement.getBoundingClientRect();
            const topShown = rect.top < 50;

            return topShown;
        }
        return false;
    }

    shouldRemoveHeightLimit() {
        if (window.innerHeight < 700) {
            this.removeHeightLimits = true;
        } else {
            this.removeHeightLimits = false;
        }
    }

    public get pageStart() {
        return !this.responsive.isXSmall() && !this.responsive.isHandsetLandscape()
            ? this.paginationStrategy.getPageStart(this._currentPage)
            : 0;
    }
    public get pageEnd() {
        return this.paginationStrategy.getPageEnd(this._currentPage);
    }

    public get pageCount() {
        return this.paginationStrategy.getPageCount();
    }

    public get isLoading() {
        return this._isLoading;
    }

    public get currentPage() {
        return this._currentPage;
    }
    public set currentPage(value: number) {
        this._currentPage = value;
    }
}

export enum AlarmLevels {
    LOW = 'LOW',
    LOWLOW = 'LOW-LOW',
    HIGHHIGH = 'HIGH-HIGH',
    HIGH = 'HIGH',
    FAIL = 'FAIL',
    ABNORMAL = 'ABNORMAL',
    UNDEFINED = '',
}

export interface FilterOptions {
    status: AlarmLevels;
    channel: string;
    occurrenceAt: string;
    recoveryTime: string;
}

export enum CalendarOptions {
    OCCURRENCE = 'occurrence',
    RECOVERY = 'recovery',
}
